import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ProductLineItemCardV2 } from '@sg/garnish';
import { useLineItemModifierIngredients } from '@sg/shared-fe/features/ordering';

import { useFeatureFlag } from '@order/LaunchDarkly';
import {
  useLineItemDescription,
  useLineItemIngredients,
  useLineItemName,
  useLineItemPrice,
} from '@order/shared/hooks';

import { type PartialLineItem, type PartialProduct } from '../../../../graphql';

/**
 * This component is a wrapper for the {ProductLineItemCardV2} from garnish.
 * It loads the messages and sets up the intermediate callbacks for it.
 */
export const BagLineItem = (props: BagLineItemProps) => {
  const {
    lineItem,
    isDisabled,
    startChangingLineItemQuantity,
    stopChangingLineItemQuantity,
    changeLineItemQuantity,
    onPress,
  } = props;

  // ─── Line Item Info ───────────────────────────────────────────────────────

  const { id: lineItemId = '', quantity = 0, product } = lineItem;

  const isModifiable = Boolean(product?.isModifiable);
  const productId = product?.id ?? '';
  const productName = useLineItemName(product, lineItem);
  const lineItemPrice = useLineItemPrice(lineItem);
  const lineItemDescription = useLineItemDescription(lineItem);
  const lineItemAssetUrl = product?.asset?.url ?? '';

  const isDetailedModificationsEnabled = useFeatureFlag(
    'CELS-5031-detailed-line-item-modifications-enabled',
  );

  const showLabels = !product.isCustom;

  const { addedIngredients: legacyAddedIngredients, removedIngredients: legacyRemovedIngredients } =
    useLineItemIngredients(lineItem);

  const { addedIngredientsText, removedIngredientsText } = useLineItemModifierIngredients({
    selectedModifiers: lineItem.selectedModifiers,
    showLabels,
    product,
  });

  // ─── Derived ────────────────────────────────────────────────────────────────

  const addedIngredients = isDetailedModificationsEnabled
    ? EMPTY_INGREDIENTS
    : legacyAddedIngredients;
  const removedIngredients = isDetailedModificationsEnabled
    ? EMPTY_INGREDIENTS
    : legacyRemovedIngredients;

  // ─── Localization ────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const accessibilityLabel = formatMessage(messages.accessibilityLabel);
  const addLabel = formatMessage(messages.addLabel);
  const removeLabel = formatMessage(messages.removeLabel);
  const currentQuantityA11y = formatMessage(messages.currentQuantityA11y);
  const increaseQuantityA11y = formatMessage(messages.increaseQuantityA11y);
  const decreaseQuantityA11y = formatMessage(messages.decreaseQuantityA11y);

  // ─── Callbacks ────────────────────────────────────────────────────────────

  const handleQuantityChange = useCallback(
    (updatedQuantity: number) => {
      changeLineItemQuantity({
        lineItemId,
        productId,
        quantity: updatedQuantity,
      });
    },
    [lineItemId, productId, changeLineItemQuantity],
  );

  const handleQuantityChangeStart = useCallback(() => {
    startChangingLineItemQuantity(lineItemId);
  }, [startChangingLineItemQuantity, lineItemId]);

  const handleQuantityChangeEnd = useCallback(() => {
    stopChangingLineItemQuantity(lineItemId);
  }, [stopChangingLineItemQuantity, lineItemId]);

  const handleOnPress = useCallback(() => {
    if (!isModifiable) return;

    onPress(lineItemId, product);
  }, [product, lineItemId, isModifiable, onPress]);

  // ─── Stop Update On Unmount ───────────────────────────────────────────────

  useEffect(() => {
    return () => {
      handleQuantityChangeEnd();
    };
  }, [handleQuantityChangeEnd]);

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <ProductLineItemCardV2
      productName={productName}
      price={lineItemPrice}
      cloudinaryImageUrl={lineItemAssetUrl}
      addedIngredients={addedIngredients}
      removedIngredients={removedIngredients}
      addedIngredientsText={isDetailedModificationsEnabled ? addedIngredientsText : undefined}
      removedIngredientsText={isDetailedModificationsEnabled ? removedIngredientsText : undefined}
      description={lineItemDescription}
      quantity={quantity}
      isDisabled={isDisabled}
      isModifiable={isModifiable}
      isCustom={product.isCustom}
      accessibilityLabel={accessibilityLabel}
      addLabel={addLabel}
      removeLabel={removeLabel}
      currentQuantityA11y={currentQuantityA11y}
      increaseQuantityA11y={increaseQuantityA11y}
      decreaseQuantityA11y={decreaseQuantityA11y}
      showLabels={showLabels}
      onQuantityChangeStart={handleQuantityChangeStart}
      onQuantityChangeEnd={handleQuantityChangeEnd}
      onQuantityChange={handleQuantityChange}
      onPress={handleOnPress}
    />
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  accessibilityLabel: {
    defaultMessage: 'Open customization',
    description: 'Bag | Product Line Item | Accessibility label',
  },
  addLabel: {
    defaultMessage: 'Add',
    description: 'Bag | Product Line Item | Add label',
  },
  removeLabel: {
    defaultMessage: 'Remove',
    description: 'Bag | Product Line Item | Remove label',
  },
  currentQuantityA11y: {
    defaultMessage: 'Current product quantity',
    description: 'Line Item Quantity Stepper | Current Quantity | a11y',
  },
  increaseQuantityA11y: {
    defaultMessage: 'Increase product quantity',
    description: 'Line Item Quantity Stepper | Increase Quantity | a11y',
  },
  decreaseQuantityA11y: {
    defaultMessage: 'Decrease product quantity',
    description: 'Line Item Quantity Stepper | Decrease Quantity | a11y',
  },
});

// ─── Constants ──────────────────────────────────────────────────────────────

const EMPTY_INGREDIENTS: ReadonlyArray<{ name?: string }> = [];

// ─── Types ──────────────────────────────────────────────────────────────────

type BagLineItemProps = Readonly<{
  lineItem: LineItem;
  isDisabled: boolean;
  startChangingLineItemQuantity: (lineItemId: string) => void;
  stopChangingLineItemQuantity: (lineItemId: string) => void;
  changeLineItemQuantity: (params: ChangeLineItemQuantityParams) => void;
  onPress: (lineItemId: string, product: PartialProduct) => void;
}>;

type ChangeLineItemQuantityParams = {
  lineItemId: string;
  productId: string;
  quantity: number;
};

type LineItem = Omit<PartialLineItem, 'ingredients'>;
